<template>
  <AppModal
    v-if="requisiteSet"
    :use-dimness="true"
    :no-body-overflow="false"
    @close="$emit('close', false)"
  >
    <template #modal-header>
      <h3
        id="modal-headline"
        class="text-lg leading-6 font-medium text-gray-900"
      >
        {{ requisiteSet.name || 'Untitled Requirement Set' }}
      </h3>
    </template>
    <template #modal-body>
      <template v-if="enableNewRequisites">
        <RequisitesSimple
          v-if="requisiteSet && requisiteSet.requisites && requisiteSet.requisites.length"
          :value="requisiteSet.requisites.filter(r => r.showInCatalog) || []"
          :document-value="{}"
          @showModal="showModal"
        />
      </template>
      <template v-else>
        <RequisitesSimpleLegacy
          v-if="requisiteSet && requisiteSet.requisites && requisiteSet.requisites.length"
          :value="requisiteSet.requisites.filter(r => r.showInCatalog) || []"
          :document-value="{}"
          @showModal="showModal"
        />
      </template>
      <CourseSetModal
        :course-set="selectedCourseSet"
        :course-list="courseList"
        @close="closeCourseSetModal"
      />
    </template>
    <template #modal-footer>
      <span class="mt-3 flex w-full rounded-md shadow-sm sm:mt-0 sm:w-auto">
        <button
          type="button"
          aria-label="Close modal"
          class="inline-flex justify-center w-full rounded-md border border-gray-300 px-4 py-2 bg-white text-base leading-6 font-medium text-gray-700 shadow-sm hover:text-gray-500 focus:outline-none focus:border-blue-300 focus:shadow-outline-blue transition ease-in-out duration-150 sm:text-sm sm:leading-5"
          @click="$emit('close', false)"
        >
          Close
        </button>
      </span>
    </template>
  </AppModal>
</template>

<script>
import { inject } from '@vue/composition-api'
import { mapState, mapGetters } from 'vuex'
import _sortBy from 'lodash/sortBy'
import AppModal from '@/components/AppModal'
import CourseSetModal from '@/components/CourseSetModal'
import useCourses from '@/composables/useCourses'
import RequisitesSimple from '@/components/Fields/Requisites/RequisitesSimple/RequisitesSimple'
import RequisitesSimpleLegacy from '@/components/Fields/Requisites/LegacyRequisites/RequisitesSimple/RequisitesSimple'
import { generateCourseLabel } from '@/utils/dataLabels'
import { courseNameWithFallbacks } from '@/utils/entityNames'

export default {
  components: {
    AppModal,
    RequisitesSimple,
    RequisitesSimpleLegacy,
    CourseSetModal,
  },
  model: { prop: 'requisiteSet', event: 'close' },
  props: {
    requisiteSet: {
      type: [Object, Boolean],
      required: true,
    },
  },
  setup(props, { root }) {
    const { goToCourse } = useCourses(root)
    const enableNewRequisites = inject('enableNewRequisites')
    return {
      goToCourse,
      generateCourseLabel,
      courseNameWithFallbacks,
      enableNewRequisites,
    }
  },
  data() {
    return {
      selectedCourseSet: false,
      isLoading: false,
      courseSetCoursesData: {},
      itemDetails: false,
    }
  },
  computed: {
    ...mapState('settings', ['school', 'coursePreviewTemplate']),
    ...mapGetters('settings', [
      'effectiveDatesRangeAsString',
      'hideCourseModal',
      'hideProgramModal',
      'showCreditsOnCourseModal',
    ]),
    courseList() {
      if (!this.selectedCourseSet) return []
      const isDynamicCourseSet = this.selectedCourseSet.type === 'dynamic'
      return (
        (isDynamicCourseSet && this.selectedCourseSet.dynamicCourseList) ||
        this.selectedCourseSet.courseList
      )
        .map((courseId) => {
          return this.courseSetCoursesData[courseId]
        })
        .filter(Boolean)
    },
    hasChildren() {
      if (!this.nestedStructure) return false
      return (this.nestedStructure.rules || []).some(
        (rule) => rule.rules && rule.rules.length
      )
    },
    courseListSorted() {
      return _sortBy(this.courseList, ['code', 'name', 'longName'])
    },
    nestedStructure() {
      return this.requisiteSet.structure
    },
    coursesObject() {
      return this.courseListSorted.reduce((acc, course) => {
        acc[course.courseGroupId] = course
        return acc
      }, {})
    },
  },
  methods: {
    showModal({ type, details }) {
      if (type === 'course' || type === 'program') {
        if (!details.id || !details.path) return
        return this.$router.push(`/${details.path}/${encodeURIComponent(details.id)}`)
      } else if (type === 'courseSet') {
        if (!details.courseSet) return
        this.selectedCourseSet = details.courseSet

        if (details.courseSet.courseSetGroupId)
          this.loadCourseSetWithRelatedData(details.courseSet.courseSetGroupId)
      }
    },
    closeCourseSetModal() {
      this.selectedCourseSet = false
      this.courseSetCoursesData = {}
    },
    async loadCourseSetWithRelatedData(courseSetId) {
      this.isLoading = true
      try {
        const data = await this.$axios.$get(
          `/cm/${this.school}/courseSets`,
          {
            params: {
              courseSetGroupIds: [courseSetId],
              includeRelatedData: true,
              effectiveDatesRange: `${this.effectiveDatesRangeAsString}`,
              courseColumns: ['code', 'subjectCode', 'longGame', 'name'],
            },
          }
        )
        if (data.courses) {
          this.courseSetCoursesData = Object.values(data.courses).reduce(
            (coursesById, course) => {
              coursesById[course.courseGroupId] = course
              return coursesById
            },
            {}
          )
        }
      } catch (e) {
        this.$bugsnag.notify(e)
        console.warn(e)
      }
      this.isLoading = false
    },
  },
}
</script>
